import { Box, Button, Flex } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';
import Constitution from '../images/constitution.webp';
import { Background, Parallax } from 'react-parallax';
import { useNavigate } from 'react-router-dom';

// Create motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionButton = motion(Button);

export default function Banner() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // How far (px) the background moves as the banner scrolls past
  const strength = isMobile ? 150 : 200;

  return (
	<Box
		width={"100%"}
	>
		<Parallax
			strength={strength}
			// Instead of the library's centered image we draw it ourselves: exactly the banner's
			// size plus the parallax travel, anchored to the top so "We the People" stays in view
			bgStyle={{ top: 0, width: "100%", height: `calc(100% + ${strength}px)` }}
		>
			<Background>
				<Box
					position={"absolute"}
					inset={0}
					backgroundImage={`url(${Constitution})`}
					backgroundSize={"cover"}
					backgroundPosition={"center top"}
					filter={"blur(1px)"}
					// Hides the soft, see-through edges the blur creates
					transform={"scale(1.02)"}
				/>
			</Background>

			<Flex
				width={"100%"}
				backgroundColor={"rgba(37, 44, 64, 0.8)"}
			>
				<Flex
					flexDirection={"column"}
					alignSelf={"flex-end"}
					// Same content width as the other home sections
					w={{ base: "90%", lg: "80%" }}
					maxWidth={"1200px"}
					paddingY={8}
					m={"auto"}
					gap={8}
				>
					{/* Text */}
					<MotionFlex
						flexDirection={"column"}
						alignItems={"flex-start"}
						initial={{ opacity: 0, x: -50 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true, amount: 0.5 }}
						transition={{ duration: 0.7, ease: "easeOut" }}
					>
						{/* Title */}
						<MotionBox
							fontSize={isMobile ? "4xl" : "5xl"}
							fontFamily={"Martel"}
							fontWeight="900"
							textAlign="left"
							color={"white.100"}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.5 }}
							transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
						>
							Joe's got your back.
						</MotionBox>

						{/* Subtitle */}
						<MotionBox
							fontSize={"xl"}
							fontFamily={"Montserrat"}
							fontWeight="500"
							textAlign="left"
							color={"white.100"}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.5 }}
							transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
						>
							Houston's big defender is here for you.
						</MotionBox>
					</MotionFlex>

					{/* CTA Buttons */}
					<MotionFlex
						direction="row"
						alignItems="flex-start"
						flex="1"
						gap={4}
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.5 }}
						transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
					>
						<MotionButton
							size="lg"
							textColor={"white.100"}
							backgroundColor={"yellow.100"}
							borderRadius={"full"}
							fontFamily={"Montserrat"}
							// Scrolls to the Contact section at the bottom of the home page
							onClick={() => navigate("/#contact")}
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							Contact
						</MotionButton>
						<MotionButton
							size="lg"
							textColor={"white.100"}
							backgroundColor={"transparent"}
							variant={"outline"}
							borderColor={"white.100"}
							borderRadius={"full"}
							fontFamily={"Montserrat"}
							onClick={() => navigate("/about")}
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							Learn More
						</MotionButton>
					</MotionFlex>
				</Flex>
			</Flex>
		</Parallax>
	</Box>
  );
}